import React from 'react';
import { AlertCircle, RotateCw, Home } from 'lucide-react';

/**
 * Top-level React error boundary. A render-time crash in any descendant now
 * shows a branded fallback instead of blanking the entire app. The original
 * error is logged to the console for debugging.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Memouraa render error:', error, info?.componentStack);
    // Future: POST to /api/client-errors with a redacted payload.
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.assign('/');
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center px-6">
        <div className="max-w-md w-full rounded-2xl border border-champagne/20 bg-white/[0.02] p-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-error/10 text-error mb-4">
            <AlertCircle className="w-7 h-7" />
          </div>
          <h1 className="text-2xl font-serif text-text-light mb-2">Something went wrong</h1>
          <p className="text-text-muted text-sm mb-6">
            An unexpected error broke this page. Your data is safe — try reloading.
          </p>
          {process.env.NODE_ENV !== 'production' && this.state.error?.message && (
            <pre className="text-left text-xs text-error/80 bg-error/5 border border-error/20 rounded-lg p-3 mb-6 overflow-x-auto">
              {this.state.error.message}
            </pre>
          )}
          <div className="flex gap-3 justify-center">
            <button
              onClick={this.handleReload}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-champagne text-midnight font-medium text-sm hover:bg-champagne-light"
              data-testid="error-boundary-reload"
            >
              <RotateCw className="w-4 h-4" /> Reload
            </button>
            <button
              onClick={this.handleHome}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-champagne/30 text-champagne text-sm hover:bg-champagne/10"
              data-testid="error-boundary-home"
            >
              <Home className="w-4 h-4" /> Home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
